import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { apiFetch, onSessionChange, sessionBootstrap, setAccessToken } from '../lib/api'
import type { StaffAuthResponse, StaffDto } from '../lib/staff-api'
import { queryClient } from './query-client'

type Status = 'loading' | 'authenticated' | 'anonymous'

interface AuthContextValue {
  status: Status
  staff: StaffDto | null
  signIn: (result: StaffAuthResponse) => void
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<StaffDto | null>(null)
  const [status, setStatus] = useState<Status>('loading')

  useEffect(() => {
    let cancelled = false

    const unsubscribe = onSessionChange((next) => {
      if (cancelled) return
      setStaff((next as StaffDto | null) ?? null)
      setStatus(next ? 'authenticated' : 'anonymous')
    })

    void sessionBootstrap.then((ok) => {
      if (!cancelled && !ok) setStatus('anonymous')
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const signIn = useCallback((result: StaffAuthResponse) => {
    setAccessToken(result.accessToken)
    setStaff(result.staff)
    setStatus('authenticated')
  }, [])

  const signOut = useCallback(async () => {
    try {
      await apiFetch<void>('/admin/auth/logout', { method: 'POST', retryOnUnauthorized: false })
    } finally {
      setAccessToken(null)
      setStaff(null)
      setStatus('anonymous')
      queryClient.clear()
    }
  }, [])

  const value = useMemo(
    () => ({ status, staff, signIn, signOut }),
    [status, staff, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
